<template>
    <div class="kliniken-table">
        <div ref="tableWrapper" class="table-wrapper">
            <div class="scroll-icon" v-if="items.length > 0 && showScrollIcon">
                <div>&#x2194;</div>
            </div>
            <table :class="{ 'grey-background': showGreyBackground }">
                <thead class="kliniken-table-headers">
                    <tr>
                        <th v-if="showCheckboxes" class="checkbox-cell">
                            <InputCheckbox :checked="allChecked" @change="checkAll($event.target.checked)" />
                        </th>
                        <th
                            v-for="column in columns.filter((col) => col.lock === 'left')"
                            :key="'header' + column.name"
                            :class="{ 'lock-left': column.lock === 'left' }"
                        >
                            <kliniken-table-header-cell
                                :column="column"
                                :sorted="sortField"
                                :direction="direction"
                                @sort="sort"
                            />
                        </th>

                        <th v-for="column in columns.filter((col) => !col.lock)" :key="'header' + column.name">
                            <kliniken-table-header-cell
                                :column="column"
                                :sorted="sortField"
                                :direction="direction"
                                @sort="sort"
                            />
                        </th>

                        <th
                            v-for="column in columns.filter((col) => col.lock === 'right')"
                            :key="'header' + column.name"
                            :class="{ 'lock-right': column.lock === 'right' }"
                        >
                            <kliniken-table-header-cell
                                :column="column"
                                :sorted="sortField"
                                :direction="direction"
                                @sort="sort"
                            />
                        </th>
                    </tr>
                </thead>

                <tbody class="kliniken-table-body">
                    <tr
                        v-for="(item, index) in items"
                        :key="index"
                        :class="{
                            'selected-row': selectedItem === item,
                            'highlighted-row': highlightedItems.includes(item.id),
                            'table-row': true,
                        }"
                    >
                        <td v-if="showCheckboxes" class="checkbox-cell">
                            <InputCheckbox
                                v-if="!hideCheckbox(item, hideCheckboxProperty, hideCheckboxValue)"
                                :checked="checkedIds.includes(item.id)"
                                @change="idChecked({ id: item.id, value: $event.target.checked })"
                            />
                        </td>

                        <td
                            v-for="column in columns.filter((col) => col.lock === 'left')"
                            :key="column.name"
                            @click="tableRowClicked(item)"
                            :class="{ 'lock-left': column.lock === 'left' }"
                        >
                            <kliniken-table-cell :item="item" :column="column" />
                        </td>

                        <td
                            v-for="column in columns.filter((col) => !col.lock)"
                            :key="column.name"
                            @click="tableRowClicked(item)"
                            :class="column.cssClass"
                        >
                            <kliniken-table-cell :item="item" :column="column" />
                        </td>

                        <td @click="tableRowClicked(item)" class="lock-right">
                            <div>
                                <template v-for="column in columns.filter((col) => col.lock === 'right')">
                                    <template v-if="column.component">
                                        <component
                                            :key="column.name"
                                            :is="column.component"
                                            v-model="item[column.name]"
                                            :initialValue="item[column.name]"
                                            v-bind="column.props"
                                            :placeholder="'Välj ett alternativ'"
                                            @statusChanged="
                                                (newValue, initialValue) =>
                                                    handleStatusEmit(item, column.name, newValue, initialValue)
                                            "
                                            :item="item"
                                        />
                                    </template>
                                    <template v-else>
                                        <kliniken-table-cell :key="column.name" slim :item="item" :column="column" />
                                    </template>
                                </template>

                                <kliniken-dropdown-menu
                                    v-if="dropdownActions?.length > 0"
                                    :dropdownActions="dropdownActions"
                                    :item="item"
                                    @action="dropdownaction"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <template v-if="showBottom && count > 20">
            <div class="next">
                <div class="dropdown">
                    <button @click="toggleLimitDropdown = !toggleLimitDropdown" v-on-clickaway="awayLimitDropdown">
                        <img src="@/assets/arrow_aropdown.svg" width="14px" height="14px" />
                        <span> Visar {{ limitLabel }} rader</span>
                    </button>
                </div>

                <div class="limit-list">
                    <ul v-show="toggleLimitDropdown">
                        <li v-for="(l, index) in availableLimits" :key="index">
                            <a @click="changeLimit(l.value)">{{ l.label }}</a>
                        </li>
                    </ul>
                </div>
            </div>

            <kliniken-pagination
                v-if="paginationStyle === 'paging'"
                :count="count"
                :perPage="limit"
                :currentPage="currentPage"
                @pageChanged="onPageChange"
            />

            <div class="items-length">
                Visar
                <strong>
                    {{ items.length }}
                </strong>
                av
                <strong>
                    {{ count }}
                </strong>
                träffar
            </div>

            <div class="show-more" v-if="paginationStyle === 'loadmore'">
                <Button variant="standard" size="primary" :disabled="limit >= count" @click="loadMore">
                    Visa flera träffar
                </Button>
            </div>
        </template>

        <slot name="custom-bottom"></slot>
    </div>
</template>

<script>
    import { mixin as clickaway } from "vue-clickaway";
    import Button from "@/components/Button.vue";
    import KlinikenPagination from "@/components/Table/KlinikenPagination.vue";
    import InputCheckbox from "@/components/Table/InputCheckbox.vue";
    import KlinikenDropdownMenu from "@/components/Table/KlinikenDropdownMenu.vue";
    import KlinikenTableCell from "@/components/Table/KlinikenTableCell.vue";
    import KlinikenTableHeaderCell from "@/components/Table/KlinikenTableHeaderCell.vue";
    import { Draggable } from "@fullcalendar/interaction";

    export default {
        name: "KlinikenTable",
        mixins: [clickaway],
        components: {
            KlinikenTableHeaderCell,
            KlinikenTableCell,
            KlinikenDropdownMenu,
            InputCheckbox,
            Button,
            KlinikenPagination,
        },
        props: {
            showGreyBackground: {
                type: Boolean,
                default: false,
            },
            columns: {
                type: Array,
                required: true,
            },
            items: {
                type: Array,
                required: true,
                default: () => {
                    return [];
                },
            },
            actions: {
                type: Array,
            },
            hasDropdown: {
                type: Boolean,
                default: false,
            },
            dropdownActions: {
                type: Array,
                default: () => {
                    return [];
                },
            },
            selectedItem: {
                type: Object,
                required: false,
            },
            highlightedItems: {
                type: Array,
                default: () => {
                    return [];
                },
            },
            count: {
                type: Number,
                default: 0,
            },
            limit: {
                type: Number,
                default: 50,
            },
            limits: {
                type: Array,
                default: () => {
                    return [
                        { value: 20, label: "20" },
                        { value: 50, label: "50" },
                        { value: 100, label: "100" },
                        { value: 0, label: "Alla" },
                    ];
                },
            },
            showCheckboxes: {
                type: Boolean,
                required: false,
                default: false,
            },
            hideCheckboxProperty: {
                type: String,
                required: false,
            },
            hideCheckboxValue: {
                type: String,
                required: false,
            },
            showBottom: {
                type: Boolean,
                required: false,
                default: true,
            },
            paginationStyle: {
                type: String,
                default: "loadmore",
                validator: function (value) {
                    return ["loadmore", "paging"].indexOf(value) !== -1;
                },
            },
            currentPage: {
                type: Number,
                required: false,
                default: 1,
            },
            lockColumns: {
                type: Boolean,
                required: false,
                default: false,
            },
            draggable: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        data() {
            return {
                toggleLimitDropdown: false,
                checkedIds: [],
                sortField: "",
                direction: null,
                hover: false,
                showScrollIcon: this.lockColumns,
                timeOutID: null,
            };
        },

        computed: {
            limitLabel() {
                const l = this.limits.find((i) => i.value === this.limit);
                return l ? `${l.label}`.toLowerCase() : this.limit;
            },
            showLimitDropdown() {
                return !!this.$slots["custom-bottom"]; // Check if slot exists
            },
            availableLimits() {
                if (this.paginationStyle === "paging") {
                    return this.limits.filter((limit) => limit.value !== 0);
                }
                return this.limits;
            },
            selectableItemsCount() {
                if (!this.hideCheckboxProperty) {
                    return this.items.length;
                }
                return this.items.filter((item) => item[this.hideCheckboxProperty] !== this.hideCheckboxValue).length;
            },
            allChecked() {
                return this.checkedIds.length === this.selectableItemsCount && this.selectableItemsCount > 0;
            },
        },

        watch: {
            items(newVal) {
                if (newVal.length > 0 && this.showScrollIcon && !this.timeOutID) {
                    this.timeOutID = setTimeout(() => {
                        this.showScrollIcon = false;
                    }, 2500);
                }
            },
        },

        methods: {
            handleStatusEmit(item, columnName, { newValue, initialValue, resolve }) {
                this.$emit("statusChanged", {
                    operationsAnmalan: item,
                    columnName: columnName,
                    newStatusValue: newValue,
                    initialValue: initialValue,
                });
            },
            sort(field) {
                if (this.sortField === field) {
                    this.direction = this.direction === "" ? "-" : "";
                } else {
                    this.sortField = field;
                    this.direction = "-";
                }
                this.$emit("sort", `${this.direction}${this.sortField}`);
            },

            loadMore() {
                this.$emit("loadMore");
            },
            changeLimit(limit) {
                this.$emit("changeLimit", limit);
            },
            awayLimitDropdown() {
                this.toggleLimitDropdown = false;
            },
            dropdownaction({ action, item }) {
                this.$emit(action, item);
            },
            tableRowClicked(item) {
                this.$emit("tableRowClicked", item);
            },

            checkAll(value) {
                if (value) {
                    this.checkedIds = this.items
                        .filter((item) => item[this.hideCheckboxProperty] !== this.hideCheckboxValue)
                        .map((i) => i.id);
                } else {
                    this.checkedIds = [];
                }

                this.$emit("handleCheckedIds", this.checkedIds);
            },

            uncheckAll() {
                this.checkedIds = [];
            },

            idChecked({ id, value }) {
                if (!this.checkedIds.includes(id) && value) {
                    this.checkedIds.push(id);
                } else if (this.checkedIds.includes(id) && !value) {
                    this.checkedIds = this.checkedIds.filter((e) => e !== id);
                }

                this.$emit("handleCheckedIds", this.checkedIds);
            },

            onPageChange(page) {
                this.$emit("loadPage", page);
            },
            hideCheckbox(item, property, value) {
                return item[property] === value;
            },

            setupDraggable() {
                if (!this.draggable) {
                    return;
                }
                new Draggable(this.$refs.tableWrapper, {
                    itemSelector: ".fc-event",
                });
            },
        },

        mounted() {
            this.setupDraggable();
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";

    $table-cell-horizontal-padding: 0.8rem;
    $table-cell-vertical-padding: 1rem;
    $table-column-max-width: 18rem;

    .kliniken-table {
        margin-bottom: 40px;

        .table-wrapper {
            overflow-x: auto;
            height: 100%;
            position: relative;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            border-style: none;

            &.grey-background {
                background-color: $color-grey-very-light;

                .kliniken-table-body {
                    .lock-right::before,
                    .lock-left::before {
                        background-color: $color-grey-very-light;
                    }

                    .highlighted-row .lock-right,
                    .highlighted-row .lock-left,
                    .highlighted-row .lock-right::before,
                    .highlighted-row .lock-left::before {
                        background-color: #eef1ff;
                    }

                    .selected-row .lock-right,
                    .selected-row .lock-left,
                    .selected-row .lock-right::before,
                    .selected-row .lock-left::before {
                        background-color: $color-blue-light;
                    }
                }
            }
        }

        .dropdown {
            display: flex;
            justify-content: flex-end;

            button {
                background: transparent;
                border: none;
                margin-top: 20px;
                cursor: pointer;

                span {
                    color: $color-text-grey;
                    margin-left: 2px;
                }
            }
        }

        .items-length {
            display: flex;
            justify-content: center;
            margin-top: 20px;

            strong {
                margin-left: 4px;
                margin-right: 4px;
            }
        }

        .next {
            .limit-list {
                width: 100%;
                position: relative;
                display: flex;
                justify-content: flex-end;

                ul {
                    top: -10px;
                    position: absolute;
                    z-index: 1;
                    background: #ffffff;
                    border: 1px solid #999c9e;
                    border-radius: 4px;
                    list-style-type: none;
                    padding: 0px;

                    li {
                        a {
                            display: block;
                            padding-right: 40px;
                            padding-left: 40px;
                            cursor: pointer;

                            &:hover {
                                background-color: $dropdown-item;
                            }
                        }
                    }
                }
            }
        }

        .show-more {
            display: flex;
            width: 100%;
            justify-content: center;
            margin-top: 20px;
            font-family:
                Roboto Medium,
                sans-serif;

            button {
                width: 800px;
                height: 46px;
                display: inline-block;
                text-align: center;
                font-size: 14px;
            }
        }

        .checkbox-cell {
            display: grid;
            justify-items: center;
        }

        .kliniken-table-headers {
            tr {
                th {
                    text-align: left;
                    border-bottom: 1px solid $list-border-color;
                    padding: 0.6rem $table-cell-horizontal-padding 0.6rem 0;
                    white-space: nowrap;
                }
            }

            .checkbox-cell {
                padding-right: 0;
            }
        }

        .kliniken-table-body {
            tr {
                text-align: left;
                position: relative;
                border-bottom: 1px solid $list-border-color;

                td {
                    line-height: normal;
                    max-width: $table-column-max-width;
                    padding: $table-cell-vertical-padding $table-cell-horizontal-padding;
                }

                &.highlighted-row {
                    background-color: #eef1ff;

                    td {
                        :deep(div) {
                            background-color: transparent;
                        }
                    }
                }

                &.selected-row {
                    background-color: #ebf6f8;

                    td:first-child::before {
                        border-left: 4px solid #277692;
                    }

                    td {
                        :deep(div) {
                            background: transparent !important;
                        }
                    }
                }

                &.table-row {
                    cursor: pointer;
                }
            }
        }

        .lock-left,
        .lock-right {
            position: sticky;
            background-color: $color-light;
        }

        .lock-left {
            left: 0;
        }

        .lock-right {
            right: 0;
        }

        .lock-right:not(th) {
            > div:first-child {
                display: flex;
                align-items: center;
                gap: 1rem;

                > div:last-child:not(:first-child) {
                    margin-left: auto;
                }
            }

            > div:first-child:has(div:only-of-type) {
                display: flex;
                place-items: center;
            }
        }

        .kliniken-table-body {
            // Måste vara ::before för att rendera box-shadow på sticky i Chrome
            .lock-right::before,
            .lock-left::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                // Fix för att rendera border i Chrome på Windows efter re-render
                border-bottom: 1px solid transparent;
            }

            .lock-right::before {
                box-shadow: $drop-shadow-left;
            }

            .lock-left::before {
                box-shadow: $drop-shadow-right;
            }

            .selected-row .lock-right,
            .selected-row .lock-left {
                background-color: #ebf6f8;
            }
        }

        .scroll-icon {
            position: absolute;
            top: 60px;
            left: 0;
            right: 0;
            width: 60px;
            margin-left: auto;
            margin-right: auto;
            padding: 5px;
            box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
            border-radius: 20px;
            background-color: #354052;
            color: white;
            font-size: 30px;
            text-align: center;
            animation: fadeOut 1.5s forwards;
            animation-delay: 1s;
        }
    }
</style>
